<template>
  <b-modal
    :visible="show"
    centered
    size="lg"
    :title="isEditing ? 'Edit Equipment' : 'Add Equipment'"
    modal-class="plant-equipment-modal"
    body-class="plant-equipment-modal-body"
    hide-footer
    no-close-on-backdrop
    @change="onVisibilityChange"
    @hidden="onHidden"
  >
    <b-form class="equipment-form" @submit.prevent="save">
      <b-form-group label="Name" class="equipment-field">
        <b-form-input
          v-model="form.name"
          placeholder="e.g. Bobcat E35"
          required
        />
      </b-form-group>

      <b-form-group label="Plant ID / Rego (optional)" class="equipment-field">
        <b-form-input v-model="form.plantIdRego" placeholder="e.g. E35-102" />
      </b-form-group>

      <div class="equipment-rate-grid">
        <div>
          <b-form-group label="Rate type" class="equipment-field mb-0">
            <b-form-select
              v-model="form.rateType"
              :options="[
                { value: 'hourly', text: 'Hourly' },
                { value: 'day', text: 'Day' },
              ]"
              required
            />
          </b-form-group>
        </div>
        <div>
          <b-form-group
            :label="`Default rate ($ / ${
              form.rateType === 'day' ? 'day' : 'hr'
            })`"
            class="equipment-field mb-0"
          >
            <b-form-input
              v-model="form.defaultRate"
              type="number"
              step="0.01"
              min="0"
              required
            />
          </b-form-group>
        </div>
      </div>

      <hr class="equipment-divider" />

      <b-form-group label="Projects" class="equipment-projects mb-0">
        <b-form-checkbox
          v-model="form.appliesToAll"
          class="equipment-all-toggle"
        >
          Applies to all projects
        </b-form-checkbox>
        <project-access-selector
          v-model="form.projectIds"
          :disabled="form.appliesToAll"
        />
      </b-form-group>

      <div
        v-if="!form.appliesToAll && form.projectIds.length > 0"
        class="rate-overrides mt-3"
      >
        <h6 class="font-weight-bold mb-2">Rate overrides</h6>
        <small class="text-muted d-block mb-2">
          Override the default rate for a specific project. Leave blank to use
          the default rate.
        </small>
        <div
          v-for="projectId in form.projectIds"
          :key="projectId"
          class="rate-override-row"
        >
          <span class="rate-override-name">{{
            projectNameById[projectId] || `Project #${projectId}`
          }}</span>
          <b-form-input
            v-model="form.rateOverrides[projectId]"
            type="number"
            step="0.01"
            min="0"
            placeholder="Use default rate"
            class="rate-override-input"
          />
        </div>
      </div>

      <div class="equipment-actions">
        <b-button variant="invisible" @click="close">Cancel</b-button>
        <b-button
          type="submit"
          variant="primary"
          :disabled="
            saving ||
            !form.name ||
            !form.defaultRate ||
            !(form.appliesToAll || form.projectIds.length > 0)
          "
        >
          <b-spinner v-if="saving" small class="mr-1" />
          {{ isEditing ? 'Save Changes' : 'Add Equipment' }}
        </b-button>
      </div>
    </b-form>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import ProjectAccessSelector from '@/components/forms/ProjectAccessSelector.vue'

export default {
  name: 'PlantEquipmentModal',

  components: { ProjectAccessSelector },

  props: {
    show: { type: Boolean, default: false },
    // null => create mode; an item object => edit mode
    item: { type: Object, default: null },
  },

  data() {
    return {
      saving: false,
      projectNameById: {},
      form: this.emptyForm(),
    }
  },

  computed: {
    isEditing() {
      return !!(this.item && this.item.id)
    },
  },

  watch: {
    show(open) {
      if (open) this.initialise()
    },
    'form.projectIds'(next) {
      // Prune stale overrides for projects no longer selected.
      const allowed = new Set(next)
      Object.keys(this.form.rateOverrides).forEach((id) => {
        if (!allowed.has(Number(id))) this.$delete(this.form.rateOverrides, id)
      })
    },
  },

  created() {
    this.loadProjectNames()
  },

  methods: {
    ...mapActions('plantEquipment', [
      'createPlantEquipment',
      'updatePlantEquipment',
    ]),

    emptyForm() {
      return {
        name: '',
        plantIdRego: '',
        rateType: 'hourly',
        defaultRate: '',
        appliesToAll: false,
        projectIds: [],
        rateOverrides: {},
      }
    },

    async loadProjectNames() {
      try {
        const { data } = await this.$api.get('projects', {
          params: { limit: 500, page: 1, is_archived: 0 },
        })
        const items = (data && data.data) || []
        const map = {}
        items.forEach((p) => {
          map[p.id] = p.name
        })
        this.projectNameById = map
      } catch (err) {
        this.projectNameById = {}
      }
    },

    initialise() {
      if (this.isEditing) {
        const overrides = {}
        ;(this.item.projects || []).forEach((p) => {
          if (p.rate_override !== null && p.rate_override !== undefined) {
            overrides[p.id] = p.rate_override
          }
        })
        this.form = {
          name: this.item.name || '',
          plantIdRego: this.item.plant_id_rego || '',
          rateType: this.item.rate_type || 'hourly',
          defaultRate:
            this.item.default_rate != null
              ? String(this.item.default_rate)
              : '',
          // Zero pivot rows means "all projects" — the API surfaces that as
          // applies_to_all_projects. Keep it as real state instead of
          // flattening it into an empty projectIds array, which renders as
          // "0 selected" and reads as unassigned.
          appliesToAll: !!this.item.applies_to_all_projects,
          projectIds: (this.item.projects || []).map((p) => p.id),
          rateOverrides: overrides,
        }
      } else {
        this.form = this.emptyForm()
      }
    },

    async save() {
      if (!this.form.name || !this.form.defaultRate) return
      this.saving = true

      const rateOverrides = {}
      this.form.projectIds.forEach((id) => {
        const val = this.form.rateOverrides[id]
        if (val !== undefined && val !== null && val !== '') {
          rateOverrides[id] = Number(val)
        }
      })

      // An empty project_ids sync() is exactly how the API stores "all
      // projects", so all-projects posts no ids — and per-project overrides
      // are meaningless at that scope.
      const payload = {
        name: this.form.name,
        plant_id_rego: this.form.plantIdRego || null,
        rate_type: this.form.rateType,
        default_rate: Number(this.form.defaultRate),
        project_ids: this.form.appliesToAll ? [] : this.form.projectIds,
        rate_overrides: this.form.appliesToAll ? {} : rateOverrides,
      }

      try {
        if (this.isEditing) {
          await this.updatePlantEquipment({ id: this.item.id, ...payload })
        } else {
          await this.createPlantEquipment(payload)
        }
        this.$bvToast.toast(
          this.isEditing ? 'Equipment updated.' : 'Equipment added.',
          { title: 'Success', variant: 'success', autoHideDelay: 3000 },
        )
        this.$emit('saved')
        this.close()
      } catch (err) {
        const message =
          (err.response && err.response.data && err.response.data.message) ||
          'Failed to save equipment.'
        this.$bvToast.toast(message, {
          title: 'Oops.. Something went wrong!',
          variant: 'danger',
          autoHideDelay: 4000,
        })
      } finally {
        this.saving = false
      }
    },

    onVisibilityChange(visible) {
      if (!visible) this.$emit('update:show', false)
    },

    close() {
      this.$emit('update:show', false)
    },

    onHidden() {
      this.$emit('update:show', false)
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .plant-equipment-modal .modal-content {
  overflow: hidden;
  border: 0;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.2);
}

::v-deep .plant-equipment-modal .modal-header {
  align-items: center;
  padding: 20px 24px 12px;
  border-bottom: 0;
}

::v-deep .plant-equipment-modal .modal-title {
  color: #111827;
  font-size: 22px;
  font-weight: 700;
}

::v-deep .plant-equipment-modal-body {
  padding: 12px 24px 24px;
}

.equipment-field {
  margin-bottom: 16px;
}

.equipment-field ::v-deep legend,
.equipment-field ::v-deep label,
.equipment-projects ::v-deep legend {
  padding: 0;
  margin-bottom: 7px;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.25;
}

.equipment-field ::v-deep .form-control,
.equipment-field ::v-deep .custom-select {
  width: 100%;
  height: 44px;
  min-height: 44px;
  border-color: #d7dee8;
  border-radius: 8px;
  box-shadow: none;
}

.equipment-field ::v-deep .form-control:focus,
.equipment-field ::v-deep .custom-select:focus {
  border-color: #006cff;
  box-shadow: 0 0 0 3px rgba(0, 108, 255, 0.12);
}

.equipment-rate-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.equipment-divider {
  margin: 22px 0 20px;
  border-color: #e5eaf2;
}

.equipment-all-toggle {
  margin-bottom: 10px;
}

.equipment-projects ::v-deep .project-access-selector > .form-control {
  height: 44px;
  min-height: 44px;
  margin-bottom: 10px !important;
  border-color: #d7dee8;
  border-radius: 8px;
  box-shadow: none;
}

.equipment-projects ::v-deep .project-access-selector > .form-control:focus {
  border-color: #006cff;
  box-shadow: 0 0 0 3px rgba(0, 108, 255, 0.12);
}

.equipment-projects ::v-deep .project-list {
  max-height: 220px;
  padding: 10px 12px !important;
  border-color: #d7dee8 !important;
  border-radius: 8px !important;
  background: #fff;
}

.equipment-projects ::v-deep .project-list .custom-control {
  min-height: 28px;
  padding-top: 2px !important;
  padding-bottom: 2px !important;
}

.equipment-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 16px;
  margin-top: 24px;
  border-top: 1px solid #e5eaf2;
}

.equipment-actions .btn {
  min-width: 112px;
  min-height: 42px;
  border-radius: 8px;
}

.rate-overrides {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 14px;
  background: #f8fafc;
}

.rate-override-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0;
}

.rate-override-name {
  flex: 1;
  font-size: 13px;
  color: #374151;
}

.rate-override-input {
  max-width: 160px;
}

@media (max-width: 575.98px) {
  ::v-deep .plant-equipment-modal .modal-header {
    padding-right: 18px;
    padding-left: 18px;
  }

  ::v-deep .plant-equipment-modal-body {
    padding-right: 18px;
    padding-left: 18px;
  }

  .equipment-rate-grid {
    grid-template-columns: 1fr;
  }

  .equipment-actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .equipment-actions .btn {
    width: 100%;
  }

  .rate-override-row {
    align-items: stretch;
    flex-direction: column;
  }

  .rate-override-input {
    max-width: none;
  }
}
</style>
